import React from "react";
import { Helmet } from "react-helmet";
import {
  Building2,
  CalendarCheck,
  Clock,
  FileCheck2,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";

import "../assets/css/Office_hub.css";

import HeaderMenu from "../component/Navbar/headermenu";
import CustomFooter from "../component/Footer/customFooter";
import OfficeHubCTA from "../component/OfficeHub/OfficeHubCTA";

import myTimesheet from "../assets/new-images/oh1.png";
import myAttendanceLog from "../assets/new-images/oh2.png";
import manageRequests from "../assets/new-images/it4.png";
import manageTimesheets from "../assets/new-images/it5.png";

const features = [
  {
    icon: <FileCheck2 size={26} />,
    title: "Leave & Present Requests",
    description:
      "Submit leave or present requests and track them from request to approval, with remarks and full history.",
  },
  {
    icon: <CalendarCheck size={26} />,
    title: "Attendance Tracking",
    description:
      "Personal and team-wide attendance logs with present, absent, and leave breakdowns for every month.",
  },
  {
    icon: <Clock size={26} />,
    title: "Automated Timesheets",
    description:
      "Check-in and check-out times roll straight into timesheets — no manual hour entry, for you or your team.",
  },
  {
    icon: <ShieldCheck size={26} />,
    title: "Policies, in One Place",
    description:
      "Keep company policies accessible to every employee, updated centrally by managers.",
  },
];

const showcase = [
  {
    eyebrow: "My Timesheet",
    title: "Your monthly attendance, laid out clearly",
    description:
      "See present days, late arrivals, overtime, leave, and absences at a glance, with a full color-coded calendar and insights like punctuality and average daily work — all built automatically from your check-in data.",
    image: myTimesheet,
    alt: "Simplifyte My Timesheet showing a monthly report with present days, late arrivals, overtime, leave days, a color-coded calendar, and attendance insights",
  },
  {
    eyebrow: "My Attendance Log",
    title: "A full year of attendance, at a glance",
    description:
      "Browse present, absent, and leave days across every month of the year in one scrollable view, with yearly totals and attendance percentage always visible at the top.",
    image: myAttendanceLog,
    alt: "Simplifyte Attendance Log showing a yearly calendar grid with present, absent, and leave days marked for each month, plus yearly attendance stats",
  },
  {
    eyebrow: "Manage Requests",
    title: "Approve leave and present requests without the back-and-forth",
    description:
      "See every leave and present request from your team in one queue, with requester, remarks, and status — approve or reject in a click, with a full history of who actioned what and when.",
    image: manageRequests,
    alt: "Simplifyte Manage Requests showing a table of leave and present requests with requester, remarks, status, and action-by columns",
  },
  {
    eyebrow: "Manage Timesheets",
    title: "Your whole team's attendance, one screen",
    description:
      "Check-in and check-out times, work hours, overtime, and break time for every employee, updated automatically — with present, late, and absent counts summarized at the top so you know where your team stands before you even scroll.",
    image: manageTimesheets,
    alt: "Simplifyte Manage Timesheets showing team check-in, check-out, work time, overtime, and break time for each employee with status labels",
  },
];

const benefits = [
  "Approve or reject requests in one click, from one queue",
  "See late arrivals and overtime the moment they happen",
  "Give every employee a clear, color-coded view of their own attendance",
  "Track present, absent, and leave records across the whole year",
  "No manual timesheet entry — hours populate from check-in and check-out",
];

export default function OfficeHub() {
  return (
    <>
      <Helmet>
        <title>Office Hub, Simplified | Simplifyte</title>
      </Helmet>
      <HeaderMenu />
      <div className="OfficeHub_class_main">
        {/* Hero */}
        <section className="OfficeHub_class_hero">
          <div className="OfficeHub_class_container">
            <div className="OfficeHub_class_hero_content">
              <div className="OfficeHub_class_icon_box">
                <Building2 size={34} />
              </div>

              <h1 className="OfficeHub_class_heading">
                Office Hub, Simplified
              </h1>

              <p className="OfficeHub_class_subheading">
                Manage requests, attendance, and timesheets in one
                centralized hub — leave requests, attendance logs, and
                timesheets, seamlessly for both employees and managers.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="OfficeHub_class_features_section">
          <div className="OfficeHub_class_container">
            <h2 className="OfficeHub_class_section_title OfficeHub_class_center">
              Everything your office needs, in one hub
            </h2>

            <div className="OfficeHub_class_features_grid">
              {features.map((feature, index) => (
                <div key={index} className="OfficeHub_class_feature_card">
                  <div className="OfficeHub_class_feature_icon">
                    {feature.icon}
                  </div>

                  <h3 className="OfficeHub_class_feature_title">
                    {feature.title}
                  </h3>

                  <p className="OfficeHub_class_feature_description">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Showcase */}
        <section className="OfficeHub_class_showcase_section">
          <div className="OfficeHub_class_container">
            <h2 className="OfficeHub_class_section_title OfficeHub_class_center">
              See Office Hub in action
            </h2>
            <p className="OfficeHub_class_showcase_subtitle">
              Real screens from the product — for employees tracking their
              own time, and managers keeping the whole team on track.
            </p>

            <div className="OfficeHub_class_showcase_list">
              {showcase.map((item, index) => (
                <div
                  key={index}
                  className={`OfficeHub_class_showcase_row ${
                    index % 2 === 1
                      ? "OfficeHub_class_showcase_row_reverse"
                      : ""
                  }`}
                >
                  <div className="OfficeHub_class_showcase_text">
                    <span className="OfficeHub_class_showcase_eyebrow">
                      {item.eyebrow}
                    </span>
                    <h3 className="OfficeHub_class_showcase_title">
                      {item.title}
                    </h3>
                    <p className="OfficeHub_class_showcase_description">
                      {item.description}
                    </p>
                  </div>

                  <div className="OfficeHub_class_showcase_media">
                    <div className="OfficeHub_class_showcase_frame_outer">
                      <div className="OfficeHub_class_showcase_frame_inner">
                        <img
                          src={item.image}
                          alt={item.alt}
                          loading="lazy"
                          className="OfficeHub_class_showcase_image"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="OfficeHub_class_benefits_section">
          <div className="OfficeHub_class_container">
            <div className="OfficeHub_class_benefits_wrapper">
              <h2 className="OfficeHub_class_section_title OfficeHub_class_center">
                Why teams run their office on Simplifyte
              </h2>

              <div className="OfficeHub_class_benefits_list">
                {benefits.map((benefit, index) => (
                  <div key={index} className="OfficeHub_class_benefit_item">
                    <CheckCircle2
                      size={24}
                      className="OfficeHub_class_check_icon"
                    />

                    <p>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

      <OfficeHubCTA />
      <CustomFooter />
    </>
  );
}
